import React from 'react';
import { X } from 'lucide-react';

export default function AttentionPanel({ attentionData, onClose, onNavigate }) {
  const {
    overdue_tasks = [],
    due_today_tasks = [],
    record_badges = {},
  } = attentionData || {};

  const unreadComms = Object.entries(record_badges)
    .filter(([, b]) => b.unread_count > 0)
    .map(([recordId, b]) => ({ recordId, ...b }))
    .sort((a, b) => new Date(b.latest_at) - new Date(a.latest_at));

  const totalItems = overdue_tasks.length + due_today_tasks.length + unreadComms.length;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/50" onClick={onClose} />

      {/* Drawer */}
      <div className="w-80 bg-[#0e0f11] border-l border-[#2a2b2f] flex flex-col h-full shadow-2xl animate-slide-in-right">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2b2f]">
          <div className="flex items-center gap-2">
            <span className="text-xl">🔔</span>
            <span className="text-white font-semibold text-sm">Needs Attention</span>
            {totalItems > 0 && (
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                {totalItems}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">

          {unreadComms.length > 0 && (
            <Section title="📩 Unread Messages" count={unreadComms.length} color="red">
              {unreadComms.map(item => (
                <AttentionItem
                  key={item.recordId}
                  title={item.record_title || 'Unknown case'}
                  subtitle={`${item.unread_count} unread · ${timeAgo(item.latest_at)}`}
                  icon={(item.channels || []).includes('email') ? '✉️' : '💬'}
                  color="red"
                  onClick={() => { onNavigate(item.recordId); onClose(); }}
                />
              ))}
            </Section>
          )}

          {overdue_tasks.length > 0 && (
            <Section title="🔥 Overdue Tasks" count={overdue_tasks.length} color="red">
              {overdue_tasks.map(task => (
                <AttentionItem
                  key={task.id}
                  title={task.title}
                  subtitle={`${task.record_title} · ${daysOverdue(task.due_date)}d overdue`}
                  icon="🔥"
                  color="red"
                  onClick={() => { onNavigate(task.record_id); onClose(); }}
                />
              ))}
            </Section>
          )}

          {due_today_tasks.length > 0 && (
            <Section title="⏰ Due Today" count={due_today_tasks.length} color="amber">
              {due_today_tasks.map(task => (
                <AttentionItem
                  key={task.id}
                  title={task.title}
                  subtitle={task.record_title}
                  icon="⏰"
                  color="amber"
                  onClick={() => { onNavigate(task.record_id); onClose(); }}
                />
              ))}
            </Section>
          )}

          {totalItems === 0 && (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <span className="text-4xl mb-3">✅</span>
              <span className="text-sm font-medium">All caught up</span>
              <span className="text-xs mt-1 text-gray-600">Nothing needs your attention</span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function Section({ title, count, color, children }) {
  const colors = { red: 'text-red-400', amber: 'text-amber-400' };
  return (
    <div className="p-3 border-b border-[#1a1b1f]">
      <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${colors[color]}`}>
        {title} <span className="opacity-70">({count})</span>
      </div>
      {children}
    </div>
  );
}

function AttentionItem({ title, subtitle, icon, color, onClick }) {
  const colors = {
    red: 'bg-red-500/10 border-red-500/20 hover:bg-red-500/20',
    amber: 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20',
  };
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-2.5 rounded-lg border mb-1.5 transition-colors ${colors[color]}`}
    >
      <div className="flex items-start gap-2">
        <span className="text-sm mt-0.5">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-white text-xs font-medium truncate">{title}</div>
          <div className="text-gray-400 text-[10px] mt-0.5 truncate">{subtitle}</div>
        </div>
        <span className="text-gray-600 text-xs mt-0.5">→</span>
      </div>
    </button>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr);
  const h = Math.floor(diff / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function daysOverdue(dateStr) {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr)) / 86400000);
}
